import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  useToast,
  Badge,
  Flex,
  IconButton,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress,
  Tag,
  TagLabel,
  TagCloseButton,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { 
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  DownloadIcon,
  AttachmentIcon,
} from '@chakra-ui/icons';
import { FaUser, FaRobot, FaStop, FaFile, FaImage, FaFileAudio, FaFileVideo, FaExpand, FaCompress, FaQuoteRight, FaArrowCircleRight } from 'react-icons/fa';
import { Markdown } from '../base/Markdown';

// Assistant UI imports
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  ThreadPrimitive,
  MessagePrimitive,
  ComposerPrimitive,
  ActionBarPrimitive,
  AttachmentPrimitive,
  useThreadViewport,
  useMessage,
  type ThreadMessageLike,
  type AppendMessage,
  type AttachmentAdapter,
} from '@assistant-ui/react';

import { Config, ConfigFormData } from '../../types/config';
import { EnhancedChatMessage, DifyCitation, DifyRetrievalReference } from '../../types/citations';
import { EnhancedProxyService as ProxyService, FileUploadResponse } from '../../services/enhancedProxyService';
import { API_BASE_URL } from '../../config/constants';
import { useAppInfoContext } from '../../contexts/AppInfoContext';
import { useAuth } from '../../contexts/AuthContext';
import DocumentThreadService from '../../services/documentThreadService';
import { DocumentThread } from '../../types/documentThread';

interface AssistantChatClientProps {
  config: Config;
  initialInputs: ConfigFormData;
  onError?: (error: string) => void;
  onClose: () => void;
  onExpandInputPanel?: () => void;
  isInputPanelCollapsed?: boolean;
}

export interface AssistantChatClientRef {
  reset: () => void;
}

// Global state for expanded citations (outside component to persist across renders)
const citationExpandedState: Record<string, boolean> = {};

// Custom Citation Display Component
const CitationDisplay: React.FC<{ 
  citations?: DifyCitation[]; 
  references?: DifyRetrievalReference[];
  messageId: string;
}> = ({ citations, references, messageId }) => {
  // Use a ref and force update pattern for persistent state
  const [, forceUpdate] = useState({});
  const isExpanded = citationExpandedState[messageId] || false;
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  
  const allReferences = [
    ...(citations || []),
    ...(references || [])
  ];
  
  if (allReferences.length === 0) return null;
  
  // Get unique documents with their scores
  const uniqueDocuments = allReferences.reduce((acc: any[], ref) => {
    const existing = acc.find(doc => doc.document_name === ref.document_name);
    if (existing) {
      existing.score = Math.max(existing.score, ref.score); // Use highest score
      existing.count++;
    } else {
      acc.push({
        document_name: ref.document_name,
        score: ref.score,
        count: 1
      });
    }
    return acc;
  }, []);
  
  return (
    <Box
      mt={3}
      p={3}
      bg={useColorModeValue('blue.50', 'blue.900')}
      borderRadius="md"
      borderLeft="4px"
      borderLeftColor="blue.500"
      fontSize="sm"
    >
      <HStack spacing={2} mb={2} justify="space-between">
        <HStack spacing={2}>
          <Box as={FaQuoteRight} color="blue.500" />
          <Text fontWeight="semibold" color="blue.700">
            References ({allReferences.length})
          </Text>
        </HStack>
        <Button
          size="xs"
          variant="ghost"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            citationExpandedState[messageId] = !isExpanded;
            forceUpdate({}); // Force re-render
          }}
          rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </Button>
      </HStack>
      
      {!isExpanded ? (
        // Collapsed view - show document names as pills with scores
        <Flex wrap="wrap" gap={2}>
          {uniqueDocuments.map((doc, index) => (
            <HStack
              key={`doc-${index}`}
              spacing={1}
              bg={cardBg}
              px={3}
              py={1.5}
              borderRadius="full"
              borderWidth="1px"
              borderColor={borderColor}
              fontSize="xs"
            >
              <Text color={mutedColor} fontWeight="medium">
                📄 {doc.document_name} {doc.count > 1 && `(${doc.count})`}
              </Text>
              <Badge
                colorScheme={doc.score > 0.8 ? 'green' : doc.score > 0.6 ? 'yellow' : doc.score > 0.1 ? 'blue' : 'gray'}
                fontSize="2xs"
                borderRadius="full"
              >
                {Math.round(doc.score * 100)}%
              </Badge>
            </HStack>
          ))}
        </Flex>
      ) : (
        // Expanded view - show full content
        <VStack spacing={2} align="stretch">
          {allReferences.slice(0, 5).map((ref, index) => (
            <Box
              key={`${ref.segment_id || index}`}
              p={2}
              bg={cardBg}
              borderRadius="sm"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <HStack justify="space-between" align="start" spacing={3}>
                <VStack align="start" spacing={1} flex="1">
                  <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                    📄 {ref.document_name}
                  </Text>
                  <Text fontSize="xs" noOfLines={3} color="gray.600">
                    {ref.content}
                  </Text>
                </VStack>
                <Tooltip label={`Relevance Score: ${ref.score.toFixed(4)} (${(ref.score * 100).toFixed(1)}%)`}>
                  <Badge
                    colorScheme={ref.score > 0.8 ? 'green' : ref.score > 0.6 ? 'yellow' : ref.score > 0.1 ? 'blue' : 'gray'}
                    fontSize="2xs"
                    px={2}
                  >
                    {Math.round(ref.score * 100)}%
                  </Badge>
                </Tooltip>
              </HStack>
            </Box>
          ))}
          
          {allReferences.length > 5 && (
            <Text fontSize="xs" color={mutedColor} textAlign="center">
              ... and {allReferences.length - 5} more references
            </Text>
          )}
        </VStack>
      )}
    </Box>
  );
};

// User Message Component
const UserMessage: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const userBg = useColorModeValue('blue.50', 'blue.900');
  
  return (
    <MessagePrimitive.Root>
      <Box w="full" py={4}>
        <Flex justify="flex-end">
          <HStack spacing={3} maxW="70%" align="flex-start" flexDirection="row-reverse">
            <Avatar size="sm" bg="blue.500" icon={<FaUser />} />
            <VStack align="end" spacing={1}>
              <Box
                bg={userBg}
                p={3}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                minW="120px"
              >
                <MessagePrimitive.Content />
              </Box>
              <Text fontSize="xs" color={mutedColor}>
                {new Date().toLocaleTimeString()}
              </Text>
            </VStack>
          </HStack>
        </Flex>
      </Box>
    </MessagePrimitive.Root>
  );
};

// Custom hook for copy/download functionality
const useMessageActions = () => {
  const toast = useToast();

  const handleCopy = useCallback(async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast({
        title: 'Copied to clipboard',
        description: 'Message has been copied.',
        status: 'success',
        duration: 1200,
        isClosable: true,
      });
    } catch (error) {
      console.error('Copy error:', error);
      toast({
        title: 'Copy failed',
        description: 'Failed to copy the message.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [toast]);

  const handleDownload = useCallback(async (content: string, format: 'md' | 'docx' | 'pdf') => {
    if (!content.trim()) return;
    
    try {
      // Generate filename with timestamp
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).replace(/\//g, '-');
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit' 
      }).replace(/:/g, '-');
      const timestamp = `${dateStr}_${timeStr}`;
      
      const filename = `Chat_Message_${timestamp}.${format}`;
      
      let blob: Blob;
      
      if (format === 'md') {
        blob = new Blob([content], { type: 'text/markdown' });
      } else {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/convert/${format}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: content
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Conversion failed: ${response.status} - ${errorText}`);
        }
        
        blob = await response.blob();
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: `Message downloaded as ${format.toUpperCase()}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Failed to download the message.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  return { handleCopy, handleDownload };
};

// Assistant Message Component
const AssistantMessage: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const assistantBg = useColorModeValue('gray.50', 'gray.700');
  const { handleCopy, handleDownload } = useMessageActions();
  
  // Get current message data from assistant-ui context
  const message = useMessage();

  return (
    <MessagePrimitive.Root>
      <MessagePrimitive.Content
        components={{
          Text: ({ text }: { text: string }) => {
            // Only render the entire message if there's actual text content
            if (!text || text.trim().length === 0) {
              return null;
            }
            
            return (
              <Box w="full" py={4}>
                <Flex justify="flex-start">
                  <HStack
                    spacing={3}
                    maxW="85%"
                    align="flex-start"
                  >
                    <Avatar size="sm" bg="gray.500" icon={<FaRobot />} />
                    <VStack align="start" spacing={1} flex="1">
                      <Box
                        bg={assistantBg}
                        p={4}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        position="relative"
                        minW="300px"
                        w="full"
                        role="group"
                      >
                        {/* Use Dify-style Markdown component for all content */}
                        <Markdown content={text} className="assistant-message-markdown" />
                
                {/* Citations Display - Only for assistant messages */}
                <Box mt={3}>
                  <CitationDisplay 
                    citations={message.metadata?.custom?.citations as DifyCitation[]} 
                    references={message.metadata?.custom?.retrieval_references as DifyRetrievalReference[]}
                    messageId={message.id}
                  />
                </Box>

                        {/* Action Bar */}
                        <AssistantActionBar onCopy={handleCopy} onDownload={handleDownload} />
                      </Box>
                      
                      <Text fontSize="xs" color={mutedColor}>
                        {new Date().toLocaleTimeString()}
                      </Text>
                    </VStack>
                  </HStack>
                </Flex>
              </Box>
            );
          },
        }}
      />
    </MessagePrimitive.Root>
  );
};

// Custom Action Bar Component for Assistant Messages
const AssistantActionBar: React.FC<{
  onCopy: (content: string) => void;
  onDownload: (content: string, format: 'md' | 'docx' | 'pdf') => void;
}> = ({ onCopy, onDownload }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      autohideFloat="single-branch"
      className="aui-action-bar"
    >
      <Box
        position="absolute"
        top={1}
        right={1}
        display="flex"
        gap={1}
        opacity={0.3}
        transition="opacity 0.2s ease"
        _hover={{ opacity: 1 }}
        _groupHover={{ opacity: 1 }}
      >
        {/* Copy Button */}
        <ActionBarPrimitive.Copy asChild>
          <IconButton
            aria-label="Copy message"
            icon={<CopyIcon />}
            size="xs"
            variant="outline"
            bg={cardBg}
            borderColor={borderColor}
            _hover={{ 
              bg: useColorModeValue('blue.50', 'blue.900'),
              borderColor: 'blue.300'
            }}
          />
        </ActionBarPrimitive.Copy>
        
        {/* Download Menu */}
        <DownloadMenu onDownload={onDownload} />
      </Box>
    </ActionBarPrimitive.Root>
  );
};

// Download Menu Component
const DownloadMenu: React.FC<{
  onDownload: (content: string, format: 'md' | 'docx' | 'pdf') => void;
}> = ({ onDownload }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label="Download message"
        icon={<DownloadIcon />}
        size="xs"
        variant="outline"
        bg={cardBg}
        borderColor={borderColor}
        _hover={{ 
          bg: useColorModeValue('green.50', 'green.900'),
          borderColor: 'green.300'
        }}
      />
      <MenuList minW="140px" zIndex={10000}>
        <MessagePrimitive.Content
          components={{
            Text: ({ text }: { text: string }) => (
              <>
                <MenuItem onClick={() => onDownload(text, 'md')}>
                  <HStack spacing={2}>
                    <Text>📄</Text>
                    <Text>Download as MD</Text>
                  </HStack>
                </MenuItem>
                <MenuItem onClick={() => onDownload(text, 'docx')}>
                  <HStack spacing={2}>
                    <Text>📝</Text>
                    <Text>Download as DOCX</Text>
                  </HStack>
                </MenuItem>
                <MenuItem onClick={() => onDownload(text, 'pdf')}>
                  <HStack spacing={2}>
                    <Text>📋</Text>
                    <Text>Download as PDF</Text>
                  </HStack>
                </MenuItem>
              </>
            ),
          }}
        />
      </MenuList>
    </Menu>
  );
};

// Sequential Progress Indicator that hides when streaming content arrives
const ProgressIndicator: React.FC<{ currentThread: DocumentThread | null }> = ({ currentThread }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const assistantBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  
  const progressSteps = [
    "Thinking...",
    "Reading documents...",
    "Compiling response...",
    "Almost done..."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % progressSteps.length);
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [progressSteps.length]);
  
  // Check if we have streaming content - if so, don't show the thinking indicator
  const hasStreamingContent = useMemo(() => {
    if (!currentThread?.isStreaming) return false;
    
    // Look for the most recent assistant message that has content
    const messages = currentThread.messages || [];
    const lastMessage = messages[messages.length - 1];
    
    // If the last message is from assistant and has content, streaming content has arrived
    return lastMessage && 
      lastMessage.role === 'assistant' && 
      lastMessage.content && 
      lastMessage.content.trim().length > 0;
  }, [currentThread?.isStreaming, currentThread?.messages]);

  // Don't show the thinking indicator if we already have streaming content
  if (hasStreamingContent) {
    return null;
  }
  
  return (
    <Box w="full" py={4}>
      <Flex justify="flex-start">
        <HStack spacing={3} maxW="85%" align="flex-start">
          <Avatar size="sm" bg="gray.500" icon={<FaRobot />} />
          <VStack align="start" spacing={1} flex="1">
            <Box
              bg={assistantBg}
              p={4}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              position="relative"
              minW="300px"
              w="full"
            >
              <HStack spacing={3} align="center">
                <Spinner size="sm" color="blue.500" />
                <Text fontSize="sm" color={textColor} fontStyle="italic">
                  {progressSteps[currentStep]}
                </Text>
              </HStack>
            </Box>
            
            <Text fontSize="xs" color={textColor}>
              Processing your request
            </Text>
          </VStack>
        </HStack>
      </Flex>
    </Box>
  );
};



export const AssistantChatClientRefactored = forwardRef<AssistantChatClientRef, AssistantChatClientProps>(({
  config,
  initialInputs,
  onError,
  onClose,
  onExpandInputPanel,
  isInputPanelCollapsed = false,
}, ref) => {
  // Thread management using existing DocumentThreadService
  const threadService = DocumentThreadService.getInstance();
  const [currentThread, setCurrentThread] = useState<DocumentThread | null>(null);
  const [currentInputs, setCurrentInputs] = useState<ConfigFormData>(initialInputs);
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadResponse[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { appInfo } = useAppInfoContext();
  const { user } = useAuth();
  const toast = useToast();

  // Color scheme
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  // Update currentInputs when initialInputs change
  useEffect(() => {
    setCurrentInputs(initialInputs);
  }, [initialInputs]);

  // Load thread when config changes - this is the main thread loading logic
  useEffect(() => {
    if (config?.id) {
      console.log('🔄 AssistantChatClientRefactored: Loading thread for document:', config.id, config.name);
      
      const thread = threadService.getThread(config.id, config.name || 'Untitled');
      console.log('📖 Thread from service:', {
        documentId: thread.documentId,
        messageCount: thread.messages.length,
        messages: thread.messages.map(m => ({ id: m.id, role: m.role, content: m.content.substring(0, 50) + '...' }))
      });
      
      setCurrentThread(thread);
      setUploadedFiles(thread.uploadedFiles || []);
      threadService.setActiveDocument(config.id);
      
      console.log('✅ AssistantChatClientRefactored: Thread loaded and state updated', {
        messageCount: thread.messages.length,
        conversationId: thread.conversationId,
        isStreaming: thread.isStreaming
      });
    } else {
      console.log('⚠️ No config.id provided');
    }
  }, [config?.id, config?.name, threadService]);

  // Convert EnhancedChatMessage to ThreadMessageLike format
  const convertMessage = useCallback((message: EnhancedChatMessage): ThreadMessageLike => {
    return {
      id: message.id,
      role: message.role,
      content: [{ type: 'text', text: message.content }],
      createdAt: message.timestamp,
      metadata: {
        ...message.metadata,
        custom: {
          citations: message.citations,
          retrieval_references: message.retrieval_references,
        },
      },
    };
  }, []);

  // Convert thread messages for assistant-ui - memoized for performance
  const assistantMessages = useMemo(() => {
    if (!currentThread) {
      console.log('🔄 No currentThread, returning empty messages');
      return [];
    }
    
    const baseMessages = currentThread.messages || [];
    console.log('🔄 Converting messages for assistant-ui:', {
      documentId: currentThread.documentId,
      messageCount: baseMessages.length,
      messages: baseMessages.map(m => ({ id: m.id, role: m.role, contentLength: m.content.length }))
    });
    
    const converted = baseMessages.map(convertMessage);
    console.log('✅ Converted messages:', converted.length);
    return converted;
  }, [currentThread?.messages, convertMessage]);

  // Let assistant-ui handle all scrolling automatically

  // Handle new messages with proper assistant-ui integration
  const handleNewMessage = useCallback(async (message: AppendMessage) => {
    if (!config?.id) return;

    const userMessage: EnhancedChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message.content[0]?.type === 'text' ? message.content[0].text : '',
      timestamp: new Date(),
      conversationId: currentThread?.conversationId,
    };

    console.log('🔄 handleNewMessage: Adding user message');
    
    // Add user message immediately
    const updatedMessages = [...(currentThread?.messages || []), userMessage];
    const updatedThread: DocumentThread = {
      ...currentThread,
      documentId: config.id,
      documentName: config.name || 'Untitled',
      messages: updatedMessages,
      isStreaming: true, // Set streaming to true to show loading state
      conversationId: currentThread?.conversationId,
      responseContent: '',
      streamingContent: '',
      error: null,
      finalTime: null,
      uploadedFiles: uploadedFiles,
      lastUpdated: new Date(),
      createdAt: currentThread?.createdAt || new Date(),
    };
    
    threadService.updateThread(config.id, updatedThread);
    setCurrentThread(updatedThread);

    try {
      // Get the latest thread state from service to avoid stale closure
      const latestThreadBeforeRequest = threadService.getThread(config.id, config.name || 'Untitled');
      const conversationIdToUse = latestThreadBeforeRequest?.conversationId || '';

      console.log('🔍 Using conversation_id for request:', conversationIdToUse || '(empty - new conversation)');

      // Convert uploaded files to API format
      const files = uploadedFiles.map(file => ({
        type: ProxyService.getFileType({ name: file.name } as File),
        transfer_method: 'local_file' as const,
        url: '',
        upload_file_id: file.id
      }));

      // Structure payload for Dify API
      const chatPayload = {
        query: userMessage.content,
        response_mode: 'streaming',
        conversation_id: conversationIdToUse,
        user: appInfo.name.replace(/\s+/g, '-'),
        files: files,
        inputs: currentInputs || {}
      };

      let citations: DifyCitation[] = [];
      let retrieval_references: DifyRetrievalReference[] = [];
      let finalStreamingContent = '';
      let streamingAssistantId = `assistant-${Date.now()}`;

      await ProxyService.processRequestWithCitations(
        config,
        chatPayload,
        (chunk: string) => {
          finalStreamingContent += chunk;
          
          console.log('🔄 Streaming chunk received, content length:', finalStreamingContent.length);
          
          // Update the streaming message in the thread with current content
          const streamingAssistantMessage: EnhancedChatMessage = {
            id: streamingAssistantId,
            role: 'assistant',
            content: finalStreamingContent,
            timestamp: new Date(),
            conversationId: currentThread?.conversationId,
          };
          
          const updatedMessagesWithStreaming = [...updatedMessages, streamingAssistantMessage];
          
          const streamingThread: DocumentThread = {
            ...currentThread,
            documentId: config.id,
            documentName: config.name || 'Untitled',
            messages: updatedMessagesWithStreaming,
            isStreaming: true,
            conversationId: currentThread?.conversationId,
            responseContent: '',
            streamingContent: finalStreamingContent,
            error: null,
            finalTime: null,
            uploadedFiles: uploadedFiles,
            lastUpdated: new Date(),
            createdAt: currentThread?.createdAt || new Date(),
          };
          
          threadService.updateThread(config.id, streamingThread);
          setCurrentThread(streamingThread);
        },
        (error: string) => {
          console.error('🚫 Error during streaming:', error);
          onError?.(error);
          
          const errorThread: DocumentThread = {
            ...currentThread,
            documentId: config.id,
            documentName: config.name || 'Untitled',
            messages: updatedMessages,
            isStreaming: false,
            responseContent: '',
            streamingContent: '',
            error: error,
            finalTime: Date.now(),
            uploadedFiles: uploadedFiles,
            lastUpdated: new Date(),
            createdAt: currentThread?.createdAt || new Date(),
          };
          threadService.updateThread(config.id, errorThread);
          setCurrentThread(errorThread);
        },
        (responseData) => {
          console.log('✅ Streaming completed with response data:', responseData);
          console.log('🆔 Received conversation_id from API:', responseData?.conversation_id);

          // Extract citations and references from enhanced response
          if (responseData?.metadata?.retrieval_references) {
            retrieval_references = responseData.metadata.retrieval_references;
            console.log('✅ Found retrieval_references:', retrieval_references.length);
          }
          if (responseData?.metadata?.citations) {
            citations = responseData.metadata.citations;
            console.log('✅ Found citations:', citations.length);
          }

          const finalMessage: EnhancedChatMessage = {
            id: responseData?.message_id || streamingAssistantId,
            role: 'assistant',
            content: finalStreamingContent,
            timestamp: new Date(),
            conversationId: responseData?.conversation_id || currentThread?.conversationId,
            citations,
            retrieval_references,
            metadata: {
              message_id: responseData?.message_id,
              task_id: responseData?.task_id,
              created_at: responseData?.created_at,
            },
          };

          // Replace the last streaming message with the final message
          // Get the latest thread state from service (not the stale closure)
          const latestThread = threadService.getThread(config.id, config.name || 'Untitled');
          const currentMessages = latestThread?.messages || [];
          const finalMessages = currentMessages.length > 0 && currentMessages[currentMessages.length - 1].role === 'assistant'
            ? [...currentMessages.slice(0, -1), finalMessage] // Replace last assistant message
            : [...currentMessages, finalMessage]; // Or add if not present
          const finalThread: DocumentThread = {
            ...currentThread,
            documentId: config.id,
            documentName: config.name || 'Untitled',
            messages: finalMessages,
            conversationId: responseData?.conversation_id || currentThread?.conversationId,
            lastMessageId: responseData?.message_id,
            isStreaming: false,
            responseContent: finalStreamingContent,
            streamingContent: '',
            error: null,
            finalTime: Date.now(),
            uploadedFiles: uploadedFiles,
            lastUpdated: new Date(),
            createdAt: currentThread?.createdAt || new Date(),
          };

          console.log('✅ Final thread updated with', finalMessages.length, 'messages');
          console.log('💾 Saving conversation_id to thread:', finalThread.conversationId);
          threadService.updateThread(config.id, finalThread);
          setCurrentThread(finalThread);
        }
      );
    } catch (error: any) {
      console.error('🚫 Error in handleNewMessage:', error);
      onError?.(error.message);
      
      const errorThread: DocumentThread = {
        ...currentThread,
        documentId: config.id,
        documentName: config.name || 'Untitled',
        messages: updatedMessages,
        isStreaming: false,
        responseContent: '',
        streamingContent: '',
        error: error.message,
        finalTime: Date.now(),
        uploadedFiles: uploadedFiles,
        lastUpdated: new Date(),
        createdAt: currentThread?.createdAt || new Date(),
      };
      threadService.updateThread(config.id, errorThread);
      setCurrentThread(errorThread);
    }
  }, [config, currentThread, currentInputs, uploadedFiles, appInfo, onError, threadService]);

  // File attachment adapter
  const attachmentAdapter: AttachmentAdapter = useMemo(() => ({
    accept: ".txt,.md,.pdf,.html,.xlsx,.xls,.docx,.csv,.pptx,.ppt,.xml,.epub,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp3,.m4a,.wav,.webm,.amr,.mp4,.mov,.mpeg,.mpga",
    async add(state: { file: File }) {
      setIsUploading(true);
      
      // Return pending attachment initially
      const pendingAttachment = {
        id: `temp-${Date.now()}`,
        type: (state.file.type.startsWith('image/') ? 'image' : 'document') as 'image' | 'document',
        name: state.file.name,
        contentType: state.file.type,
        file: state.file,
        status: { type: 'requires-action', reason: 'composer-send' } as const,
      };

      try {
        const uploadUrl = ProxyService.getUploadUrl(config.requestUrl);
        const uploadedFile = await ProxyService.uploadFile(state.file, uploadUrl, config.id);
        
        setUploadedFiles(prev => [...prev, uploadedFile]);
        
        // Update the attachment with the real ID
        return {
          ...pendingAttachment,
          id: uploadedFile.id,
        };
      } catch (error: any) {
        setIsUploading(false);
        throw new Error(`Upload failed: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    },
    async remove(attachment) {
      setUploadedFiles(prev => prev.filter(f => f.id !== attachment.id));
    },
    async send(attachment) {
      // Convert PendingAttachment to CompleteAttachment
      return {
        id: attachment.id,
        type: attachment.type,
        name: attachment.name,
        contentType: attachment.contentType,
        file: attachment.file,
        status: { type: 'complete' } as const,
        content: [{
          type: 'text' as const,
          text: `Uploaded: ${attachment.name}`,
        }],
      };
    },
  }), [config.requestUrl, config.id]);

  // Expose reset method
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (!config?.id) return;
      
      threadService.clearThread(config.id);
      setUploadedFiles([]);
      setCurrentInputs(initialInputs);
      
      const clearedThread = threadService.getThread(config.id, config.name || 'Untitled');
      setCurrentThread(clearedThread);
    }
  }), [config?.id, config?.name, initialInputs, threadService]);

  // Create the runtime with proper state management
  const runtime = useExternalStoreRuntime({
    messages: assistantMessages,
    isRunning: currentThread?.isStreaming || false,
    onNew: handleNewMessage,
    // Enable message editing and branch switching
    setMessages: useCallback((newMessages: ThreadMessageLike[]) => {
      if (!config?.id) return;
      
      console.log('🔄 setMessages called with:', newMessages.length, 'messages');
      
      // Convert back to EnhancedChatMessage format, filtering out system messages
      const convertedMessages: EnhancedChatMessage[] = newMessages
        .filter((msg) => msg.role === 'user' || msg.role === 'assistant') // Only keep user and assistant messages
        .map((msg) => {
          // Convert content to string format required by EnhancedChatMessage
          let contentString: string = '';
          if (typeof msg.content === 'string') {
            contentString = msg.content;
          } else if (Array.isArray(msg.content)) {
            // Extract text from content parts
            contentString = msg.content
              .map(part => {
                if (part.type === 'text') {
                  return part.text;
                }
                return ''; // Skip non-text content for now
              })
              .join('');
          }
          
          return {
            id: msg.id || `msg-${Date.now()}`,
            role: msg.role as 'user' | 'assistant',
            content: contentString,
            timestamp: msg.createdAt || new Date(),
            conversationId: currentThread?.conversationId,
            citations: (msg.metadata?.custom?.citations as DifyCitation[]) || [],
            retrieval_references: (msg.metadata?.custom?.retrieval_references as DifyRetrievalReference[]) || [],
            metadata: msg.metadata,
          };
        });
      
      // Update thread with new messages
      const updatedThread: DocumentThread = {
        ...currentThread,
        documentId: config.id,
        documentName: config.name || 'Untitled',
        messages: convertedMessages,
        isStreaming: false,
        responseContent: '',
        streamingContent: '',
        error: null,
        finalTime: null,
        uploadedFiles: currentThread?.uploadedFiles || [],
        lastUpdated: new Date(),
        createdAt: currentThread?.createdAt || new Date(),
      };
      
      threadService.updateThread(config.id, updatedThread);
      setCurrentThread(updatedThread);
    }, [config?.id, config?.name, currentThread, threadService]),
    convertMessage: (message: ThreadMessageLike) => message, // No conversion needed since we already convert
    adapters: {
      attachments: attachmentAdapter,
    },
  });

  // Generate conversation transcript
  const generateConversationTranscript = useCallback(() => {
    const allMessages = currentThread?.messages || [];
    if (allMessages.length === 0) return '';

    let transcript = `# Chat with ${config.name}\n\n`;
    transcript += `**Generated on:** ${new Date().toLocaleString()}\n\n`;
    transcript += `**Total Messages:** ${allMessages.length}\n\n`;
    transcript += '---\n\n';

    // Add completed messages
    allMessages.forEach((message, index) => {
      const role = message.role === 'user' ? 'You' : 'Assistant';
      const timestamp = new Date(message.timestamp).toLocaleString();
      
      transcript += `## ${role} - ${timestamp}\n\n`;
      transcript += `${message.content}\n\n`;
      
      // Add citations for assistant messages
      if (message.role === 'assistant' && (message.citations || message.retrieval_references)) {
        const allRefs = [...(message.citations || []), ...(message.retrieval_references || [])];
        if (allRefs.length > 0) {
          transcript += `**References:**\n`;
          allRefs.forEach((ref, refIndex) => {
            transcript += `${refIndex + 1}. ${ref.document_name} (Score: ${(ref.score * 100).toFixed(0)}%)\n`;
            transcript += `   "${ref.content.substring(0, 100)}..."\n`;
          });
          transcript += '\n';
        }
      }
      
      transcript += '---\n\n';
    });

    return transcript.trim();
  }, [currentThread?.messages, config.name]);

  // Generate filename for conversation download
  const generateConversationFilename = useCallback((format: string) => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    }).replace(/\//g, '-');
    const timeStr = now.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    }).replace(/:/g, '-');
    const timestamp = `${dateStr}_${timeStr}`;
    
    const baseName = config.name || 'Chat';
    return `${baseName} Chat - ${timestamp}.${format}`;
  }, [config.name]);

  // Handle conversation download
  const handleConversationDownload = useCallback(async (format: 'md' | 'docx' | 'pdf') => {
    const allMessages = currentThread?.messages || [];
    if (allMessages.length === 0) return;
    
    try {
      const transcript = generateConversationTranscript();
      const filename = generateConversationFilename(format);
      
      let blob: Blob;
      
      if (format === 'md') {
        blob = new Blob([transcript], { type: 'text/markdown' });
      } else {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/convert/${format}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: transcript
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Conversion failed: ${response.status} - ${errorText}`);
        }
        
        blob = await response.blob();
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Conversation downloaded',
        description: `Your chat transcript has been downloaded as ${format.toUpperCase()}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Failed to download the conversation.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [currentThread?.messages, generateConversationTranscript, generateConversationFilename, toast]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <Box 
        h={isFullscreen ? "100vh" : "100%"} 
        display="flex" 
        flexDirection="column" 
        overflow="hidden"
        position={isFullscreen ? 'fixed' : 'relative'} 
        top={isFullscreen ? '0' : 'auto'} 
        left={isFullscreen ? '0' : 'auto'} 
        w={isFullscreen ? '100vw' : 'auto'} 
        maxW={isFullscreen ? '100vw' : 'none'} 
        zIndex={isFullscreen ? 9999 : 'auto'} 
        borderRadius={isFullscreen ? '0' : 'md'}
        bg={cardBg}
      >
        {/* Header with title and controls */}
        <Box 
          p={4} 
          borderBottom="1px" 
          borderColor={borderColor} 
          bg={headerBg}
          flexShrink={0}
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <Box
                p={2}
                borderRadius="md"
                bg={useColorModeValue('blue.50', 'blue.900')}
                color={useColorModeValue('blue.600', 'blue.200')}
              >
                <FaRobot />
              </Box>
              <Text fontWeight="semibold" fontSize="lg" color={textColor}>
                Chat with {config.name}
              </Text>
            </HStack>
            
            <HStack spacing={2}>
              {/* Clear Button */}
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Text fontSize="sm">🧹</Text>}
                onClick={() => {
                  if (ref && 'current' in ref && ref.current) {
                    ref.current.reset();
                  }
                }}
                isDisabled={!currentThread?.messages?.length}
              >
                Clear
              </Button>

              {/* Download Conversation Button */}
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  leftIcon={<DownloadIcon />}
                  rightIcon={<ChevronDownIcon />}
                  title={`Download conversation transcript (${currentThread?.messages?.length || 0} messages)`}
                  isDisabled={!currentThread?.messages?.length}
                >
                  Download
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => handleConversationDownload('md')}>
                    <HStack spacing={2}>
                      <Text>📄</Text>
                      <Text>Download as MD</Text>
                    </HStack>
                  </MenuItem>
                  <MenuItem onClick={() => handleConversationDownload('docx')}>
                    <HStack spacing={2}>
                      <Text>📝</Text>
                      <Text>Download as DOCX</Text>
                    </HStack>
                  </MenuItem>
                  <MenuItem onClick={() => handleConversationDownload('pdf')}>
                    <HStack spacing={2}>
                      <Text>📋</Text>
                      <Text>Download as PDF</Text>
                    </HStack>
                  </MenuItem>
                </MenuList>
              </Menu>
              
              {/* Fullscreen Button */}
              <Button
                size="sm"
                variant="outline"
                onClick={toggleFullscreen}
                leftIcon={isFullscreen ? <FaCompress /> : <FaExpand />}
              >
                {isFullscreen ? 'Exit' : 'Fullscreen'}
              </Button>
            </HStack>
          </Flex>
        </Box>

        {/* Main Thread Area */}
        <Box 
          flex="1" 
          overflow="hidden" 
          display="flex"
          flexDirection="column"
        >
          <ThreadPrimitive.Root
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              width: '100%'
            }}
          >
            <ThreadPrimitive.Viewport 
              style={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column',
                overflowY: 'scroll',
                scrollBehavior: 'smooth',
                backgroundColor: 'inherit',
                padding: '1rem'
              }}
            >
              <ThreadPrimitive.Empty>
                <Box 
                  display="flex" 
                  width="100%" 
                  flexGrow={1} 
                  flexDirection="column"
                >
                  <Flex align="center" justify="center" h="full" minH="200px" flexGrow={1}>
                    <VStack spacing={4} textAlign="center">
                      <Text color={mutedColor} fontSize="lg" fontWeight="medium">
                        How can I help you today?
                      </Text>
                      <Text color={mutedColor} fontSize="sm">
                        Start a conversation with {config.name}
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
              </ThreadPrimitive.Empty>

              <ThreadPrimitive.Messages
                components={{
                  UserMessage: UserMessage,
                  AssistantMessage: AssistantMessage,
                }}
              />

              {/* Progress Indicator when assistant is thinking */}
              <ThreadPrimitive.If running>
                <ProgressIndicator currentThread={currentThread} />
              </ThreadPrimitive.If>

              <ThreadPrimitive.If empty={false}>
                <Box minH="8" flexGrow={1} />
              </ThreadPrimitive.If>

              <Box 
                position="sticky" 
                bottom={0} 
                mt={3} 
                display="flex" 
                w="full" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="end" 
                borderRadius="lg" 
                bg="inherit" 
                pb={4}
              >
                <ThreadPrimitive.ScrollToBottom>
                  <IconButton
                    aria-label="Scroll to bottom"
                    icon={<ChevronDownIcon />}
                    size="sm"
                    variant="outline"
                    borderRadius="full"
                    position="absolute"
                    top={-8}
                    bg={cardBg}
                    borderColor={borderColor}
                    _hover={{ 
                      bg: useColorModeValue('blue.50', 'blue.900'),
                      borderColor: 'blue.300'
                    }}
                    _disabled={{
                      visibility: 'hidden'
                    }}
                  />
                </ThreadPrimitive.ScrollToBottom>
              </Box>
            </ThreadPrimitive.Viewport>
          </ThreadPrimitive.Root>
        </Box>

        {/* Composer Area */}
        <Box 
          p={4} 
          borderTop="2px" 
          borderColor={borderColor} 
          bg={cardBg} 
          boxShadow="0 -2px 4px rgba(0,0,0,0.1)"
          flexShrink={0}
        >
          <ComposerPrimitive.Root>
            {/* Upload Progress */}
            {isUploading && (
              <Box w="full" mb={3}>
                <Text fontSize="xs" color={mutedColor} mb={1}>
                  Uploading files... {Math.round(uploadProgress)}%
                </Text>
                <Progress value={uploadProgress} size="sm" colorScheme="blue" />
              </Box>
            )}

            {/* Uploaded Files Display */}
            <ComposerPrimitive.Attachments
              components={{
                Attachment: () => (
                  <AttachmentPrimitive.Root>
                    <Tag size="md" variant="solid" colorScheme="blue" borderRadius="md">
                      <AttachmentPrimitive.Name />
                      <TagCloseButton as={AttachmentPrimitive.Remove} />
                    </Tag>
                  </AttachmentPrimitive.Root>
                ),
              }}
            />

            {/* Input Area */}
            <Box position="relative" w="full">
              <Box 
                position="relative"
                bg={useColorModeValue('white', 'gray.900')}
                borderColor={borderColor}
                borderWidth="2px"
                borderRadius="full"
                _focusWithin={{
                  borderColor: 'blue.500',
                  boxShadow: `0 0 0 1px blue.500`,
                }}
                _hover={{
                  borderColor: 'blue.300',
                }}
                display="flex"
                alignItems="center"
                pr={2}
              >
                {/* Attachment button */}
                <ComposerPrimitive.AddAttachment asChild>
                  <IconButton
                    aria-label="Attach files"
                    icon={<AttachmentIcon />}
                    isDisabled={isUploading}
                    variant="ghost"
                    size="sm"
                    colorScheme="gray"
                    ml={2}
                    borderRadius="full"
                  />
                </ComposerPrimitive.AddAttachment>
                
                {/* Input field */}
                <ComposerPrimitive.Input
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    flex: 1,
                    fontSize: '1rem',
                    padding: '0.5rem',
                  }}
                  placeholder="Type your question here"
                />
                
                {/* Send/Cancel button */}
                <ThreadPrimitive.If running={false}>
                  <ComposerPrimitive.Send asChild>
                    <IconButton
                      aria-label="Send message"
                      icon={<FaArrowCircleRight />}
                      colorScheme="blue"
                      size="sm"
                      isRound
                      variant="solid"
                    />
                  </ComposerPrimitive.Send>
                </ThreadPrimitive.If>
                
                <ThreadPrimitive.If running={true}>
                  <ComposerPrimitive.Cancel asChild>
                    <IconButton
                      aria-label="Stop generation"
                      icon={<FaStop />}
                      colorScheme="red"
                      size="sm"
                      isRound
                      variant="solid"
                    />
                  </ComposerPrimitive.Cancel>
                </ThreadPrimitive.If>
              </Box>
            </Box>
          </ComposerPrimitive.Root>
        </Box>
      </Box>
    </AssistantRuntimeProvider>
  );
});

AssistantChatClientRefactored.displayName = 'AssistantChatClientRefactored';